import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { deliveryPartnersService } from "@/services/deliveryPartnersService";
import { ordersService } from "@/services/ordersService";
import { storesService } from "@/services/storesService";
import { walletsService } from "@/services/walletsService";
import type { OrderFilterParams } from "@/types/order.dto";
import type { DeliveryPartnerRegisterDto, DeliveryPartnerUpdateDto } from "@/types/delivery.dto";
import type { DeliveryOrderRow } from "@/types/delivery.ui";
import { WalletOwnerType } from "@/types/enums";
import {
  mapOrderListDtosToDeliveryRows,
  mapPartnerToProfileUi,
  mapSettlementsToDeliveryEarnings,
} from "@/lib/mappers/delivery-mappers";
import { queryKeys } from "./keys";

export function useMyDeliveryProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.delivery.profile,
    queryFn: async () => {
      const profile = await deliveryPartnersService.getMyProfile();
      return mapPartnerToProfileUi(profile);
    },
    enabled,
    retry: false,
  });
}

export function useRegisterDeliveryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: DeliveryPartnerRegisterDto) => deliveryPartnersService.register(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.delivery.profile });
    },
  });
}

export function useDeliveryPartnerId() {
  const { user } = useAuth();
  const needsFetchForId = !user?.deliveryPartnerId;
  // Always load profile when the user already has a partner id (e.g. from login
  // context). Previously we skipped the fetch in that case, leaving profile null
  // and pages like /delivery/profile stuck on the loading skeleton forever.
  const shouldFetchProfile =
    Boolean(user) && (needsFetchForId || Boolean(user?.deliveryPartnerId));
  const { data: profile, isLoading } = useMyDeliveryProfile(shouldFetchProfile);

  return {
    partnerId: user?.deliveryPartnerId ?? profile?.id,
    profile,
    isLoading: shouldFetchProfile && isLoading,
  };
}

export function useDeliveryDashboard(partnerId?: number) {
  return useQuery({
    queryKey: queryKeys.delivery.dashboard(partnerId ?? 0),
    queryFn: () => deliveryPartnersService.getDashboard(partnerId!),
    enabled: Boolean(partnerId),
  });
}

async function enrichDeliveryOrders(items: Awaited<ReturnType<typeof ordersService.getDeliveryPartnerOrders>>["items"]) {
  const details = await Promise.all(
    items.map((item) => ordersService.getById(item.id).catch(() => null)),
  );

  const detailMap = new Map(
    details.filter(Boolean).map((detail) => [detail!.id, detail!]),
  );

  const storeIds = [...new Set([...detailMap.values()].map((d) => d.storeId))];
  const storeResults = await Promise.all(
    storeIds.map((id) => storesService.getById(id).catch(() => null)),
  );
  const storeAddresses = new Map(
    storeResults.filter(Boolean).map((store) => [store!.id, store!.fullAddress]),
  );

  return mapOrderListDtosToDeliveryRows(items, detailMap, storeAddresses);
}

export function useDeliveryOrders(partnerId?: number, filter: OrderFilterParams = { pageSize: 50 }) {
  return useQuery({
    queryKey: queryKeys.delivery.orders(partnerId ?? 0, filter),
    queryFn: async () => {
      const result = await ordersService.getDeliveryPartnerOrders(partnerId!, filter);
      const items = await enrichDeliveryOrders(result.items);
      return { ...result, items };
    },
    enabled: Boolean(partnerId),
  });
}

export function useDeliveryEarnings(partnerId?: number, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.delivery.earnings(partnerId ?? 0, page, pageSize),
    queryFn: async () => {
      const result = await walletsService.getSettlements({
        deliveryPartnerId: partnerId,
        ownerType: WalletOwnerType.DeliveryPartner,
        page,
        pageSize,
      });
      return {
        ...result,
        items: mapSettlementsToDeliveryEarnings(result.items),
      };
    },
    enabled: Boolean(partnerId),
  });
}

export function useAdvanceDeliveryOrderMutation(partnerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      currentStatus,
      otp,
    }: {
      orderId: number;
      currentStatus: DeliveryOrderRow["status"];
      otp?: string;
    }) => {
      switch (currentStatus) {
        case "assigned":
        case "picked_up":
          return ordersService.markPickedUp(orderId, partnerId);
        case "in_transit":
          if (!otp) throw new Error("Delivery OTP is required");
          return ordersService.markDelivered(orderId, partnerId, otp);
        default:
          throw new Error("No action available for this order status");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery", partnerId] });
    },
  });
}

export function useToggleDeliveryOnlineMutation(partnerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isOnline: boolean) => deliveryPartnersService.toggleOnline(partnerId, isOnline),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.delivery.profile });
      queryClient.invalidateQueries({ queryKey: queryKeys.delivery.dashboard(partnerId) });
    },
  });
}

export function useUpdateDeliveryProfileMutation(partnerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: DeliveryPartnerUpdateDto) =>
      deliveryPartnersService.updateProfile(partnerId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.delivery.profile });
    },
  });
}
